import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {MapCameraChangedEvent, MapMouseEvent} from '@vis.gl/react-google-maps';
import {AdvancedMarker, APIProvider, ControlPosition, Map, MapControl, useAdvancedMarkerRef} from '@vis.gl/react-google-maps';
import {GoogleMapAutocomplete} from '@Components/google-map/components/google-map-autocomplete';
import {GoogleMapHandler} from '@Components/google-map/components/google-map-handler';
import './googleMap.scss';
import type {GoogleMapProps, PlaceDetails} from '@Components/google-map/google-map.types';
import {USER_LOCATION_TIMEOUT_MS} from '@Components/google-map/google-map.types';
import {getLatLngFromPlaceDetails} from '@Components/google-map/google-map.types';
import {useGoogleMapPlace} from '@Components/google-map/google-map.hooks';
import {useDispatch} from 'react-redux';
import {onGoogleMapPlaceChange} from '@Components/google-map/google-map-slice';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getPlaceAddressByCords, getPlaceById} from './google-map.helper';
import styles from './google-map.module.scss';

export function GoogleMap({
  apiKey,
  onLocationChange,
  defaultLocation = undefined,
  isViewOnly = false,
  isAutoCompleteOnly = false,
  selectCurrentLocationAsDefault = false,
  mapStyle = {isRoundedBorder: true},
  shouldSetUserCurrentLocation = true,
  autocompleteInputFieldSize = InputFieldSize.XSMALL,
}: GoogleMapProps): ReactElement {
  const noUserSelectedLocation = {lat: 39.459502, lng: -97.696864}; // America,
  const noUserSelectedZoom = 4;
  const defaultZoom = 15;
  const dispatch = useDispatch();

  const [selectedPlace, setSelectedPlace] = useState<PlaceDetails | undefined>(useGoogleMapPlace());
  const [markerLocation, setMarkerlocation] = useState<google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | undefined | null>(undefined);
  const [searchVal, setSearchVal] = useState<string>('');
  const [zoom, setZoom] = useState<number | null>(null);
  const [mapCenter, setCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined);
  const [isUserFindMyLocationLoading, setIsUserFindMyLocationLoading] = useState<boolean>(false);
  const [isLocationUpdating, setIsLocationUpdating] = useState<boolean>(false);

  const [markerRef, marker] = useAdvancedMarkerRef();

  useEffect(() => {
    if (defaultLocation) {
      const latLng = getLatLngFromPlaceDetails(defaultLocation);
      setMarkerlocation(latLng);
      setCenter(latLng);
      setZoom(defaultZoom);
      setSearchValFromPlaceDetails(defaultLocation);
      return;
    }

    if (shouldSetUserCurrentLocation) {
      setMarkerToUserCurrentLocation();
    }
  }, [defaultLocation]);

  useEffect(() => {
    if (!marker) {
      return;
    }
    marker.position = markerLocation;

    if (!isViewOnly) {
      marker.addListener('dragend', () => {
        const {position} = marker;
        void updateMarkerLocation(position);
      });
    }
  }, [marker]);

  const updateZoom = (e: MapCameraChangedEvent): void => {
    setZoom(e.detail.zoom);
  };

  const onUpdateSearch = (value: string): void => {
    setSearchVal(value);
  };

  const setSearchValFromPlaceDetails = (place: PlaceDetails): void => {
    setSearchVal(`${place.name} - ${place.address}`);
  };

  const updateSelectedPlace = (newPlace: PlaceDetails | undefined): void => {
    setSelectedPlace(newPlace);
    dispatch(onGoogleMapPlaceChange(newPlace));

    if (onLocationChange) {
      onLocationChange(newPlace ?? undefined);
    }
  };

  async function updateMarkerLocation(
    newMarkerLocation: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | null | undefined,
    shouldSetPlace = true
  ): Promise<void> {
    setMarkerlocation(newMarkerLocation);

    if (!newMarkerLocation) {
      return;
    }

    if (!shouldSetPlace) {
      return;
    }
    const newPlaceDetails = await getPlaceAddressByCords(newMarkerLocation);

    setSearchVal(newPlaceDetails?.address ? newPlaceDetails.address : '');
    updateSelectedPlace({
      ...newPlaceDetails,
      latitude: typeof newMarkerLocation.lat === 'function' ? newMarkerLocation.lat() : newMarkerLocation.lat,
      longitude: typeof newMarkerLocation.lng === 'function' ? newMarkerLocation.lng() : newMarkerLocation.lng,
    });
  }

  const handleOnClickMap = (e: MapMouseEvent): void => {
    if (isUserFindMyLocationLoading || isLocationUpdating) {
      return;
    }
    setCenter(undefined);
    setIsLocationUpdating(true);

    if (!e.detail.placeId || !marker) {
      void updateMarkerLocation(e.detail.latLng).then(() => {
        setIsLocationUpdating(false);
      });
      return;
    }

    void getPlaceById(e.detail.placeId).then((place: PlaceDetails) => {
      setIsLocationUpdating(false);
      if (!place.longitude || !place.latitude) {
        return;
      }
      updateSelectedPlace(place);
      setSearchValFromPlaceDetails(place);
      void updateMarkerLocation(
        {
          lat: place.latitude,
          lng: place.longitude,
        },
        false
      );
    });
  };

  const onSelectPlaceFromAutoComplete = (place: PlaceDetails | undefined): void => {
    setCenter(undefined);
    updateSelectedPlace(place);

    if (place) {
      setCenter({
        lat: place.latitude,
        lng: place.longitude,
      });
      setSearchValFromPlaceDetails(place);
      void updateMarkerLocation(
        {
          lat: place.latitude,
          lng: place.longitude,
        },
        false
      );
    }
  };

  const setMarkerToUserCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      return; // Geolocation not supported by browser
    }
    setIsUserFindMyLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position: GeolocationPosition) => {
        setIsUserFindMyLocationLoading(true);
        const lngLat = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        if (selectCurrentLocationAsDefault) {
          await updateMarkerLocation(lngLat);
        }
        setCenter(lngLat);
        setZoom(defaultZoom);
        setIsUserFindMyLocationLoading(false);
      },
      () => {
        setIsUserFindMyLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: USER_LOCATION_TIMEOUT_MS,
      }
    );
  };

  const shouldLoadAutocomplete = (): boolean => {
    if (isViewOnly) {
      return false;
    }
    return !!onSelectPlaceFromAutoComplete;
  };

  const getMapContent = (): ReactElement => {
    return (
      <>
        <Map
          clickableIcons
          onClick={!isViewOnly ? handleOnClickMap : undefined}
          mapId="google-maps"
          className={`map ${mapStyle?.isRoundedBorder ? '-rounded-border' : ''} ${mapStyle?.className ? mapStyle.className : ''}`}
          defaultZoom={noUserSelectedZoom}
          zoom={zoom}
          onZoomChanged={updateZoom}
          defaultCenter={noUserSelectedLocation}
          center={mapCenter}
          onDragstart={() => {
            setCenter(undefined);
          }}
          disableDefaultUI={isAutoCompleteOnly || isViewOnly}
        >
          <AdvancedMarker draggable={!isViewOnly} ref={markerRef} position={markerLocation} />
        </Map>
        {shouldLoadAutocomplete() ? (
          <>
            <MapControl position={ControlPosition.BLOCK_END_INLINE_START}>
              <div className={`${styles.customIconUserLocationContainer} bg-neutral-0 flex-row-align-center justify-content-center`}>
                <Icon
                  isLoading={isUserFindMyLocationLoading}
                  onClick={setMarkerToUserCurrentLocation}
                  icon="icon-locate"
                  size={IconSize.SIZE_ICON_20}
                  type={IconType.GHOST}
                  shape={IconShape.SQUARE}
                  isUsedAsButton
                />
              </div>
            </MapControl>
            <GoogleMapHandler place={selectedPlace} marker={marker} />
          </>
        ) : undefined}
      </>
    );
  };

  const getAutoCompleteContent = (): ReactElement => {
    return (
      <GoogleMapAutocomplete
        isLocationUpdating={isUserFindMyLocationLoading || isLocationUpdating}
        searchVal={searchVal}
        onUpdateSearch={onUpdateSearch}
        onPlaceSelect={onSelectPlaceFromAutoComplete}
        inputFieldSize={autocompleteInputFieldSize}
      />
    );
  };

  return (
    <div className={`${isAutoCompleteOnly ? 'autocomplete-only' : ''} _full-width _full-height _position-relative`}>
      <APIProvider apiKey={apiKey} solutionChannel="GMP_devsite_samples_v3_rgmautocomplete">
        {!isViewOnly ? getAutoCompleteContent() : undefined}

        {!isAutoCompleteOnly ? getMapContent() : undefined}
      </APIProvider>
    </div>
  );
}
